import {
    addFeudFactions,
    addFeudSuperstars,
    addFeudTagTeams,
    Feud,
    getFeudFeudItem,
    getFeudMatches,
    getFeudParticipants,
    getFeudRoster,
    getFeudSegments
} from "../models/feud.js";
import {
    checkAreObjArraysEqual,
    findNameInString,
    gameLimits,
    getCurrentGame,
    getMonthsDifference,
    getRandomChoice,
    getRandomInt,
    getRosterMemberStreaks,
    getUniqueObjects,
    limitValueBetweenRange,
    myround,
    objectInArray
} from "./helpers.js";
import {getGameCurrentUserCompany} from "./games.js";
import {
    addFormerCompanyRelationship,
    generateGimmickMatch,
    getAvailableStaffMembers,
    getCompanyLatestEvent,
    getCompanyRosterByType
} from "./companies.js";
import {
    addRosterFeuds,
    addRosterSuperstars,
    getRosterFactions,
    getRosterFeuds,
    getRosterSuperstars
} from "../models/roster.js";
import {
    addKayfabeInjuryToSuperstar,
    changeSuperstarMoraleToHappy,
    checkAvailability,
    checkSuperstarBrandAvailability,
    checkSuperstarGraphicMask,
    checkSuperstarSegmentAvailability,
    getRankFromPopularity,
    getRosterMemberSuperstars,
    getSuperstarActiveFactions,
    getSuperstarChampionshipsFromCompanyChampionships,
    removeSuperstarFactionRelationships,
    removeSuperstarGraphicMask,
    removeSuperstarManagerRelationships,
    removeSuperstarTagTeams,
    retireSuperstar,
    vacateChampionships
} from "./superstars.js";
import {traitsData} from "../data/traits.js";
import {
    db,
    FeudItems,
    FeudMatchesHash,
    FeudParticipantsHash,
    Feuds,
    FeudSegmentsHash,
    GimmickMatches,
    Matches,
    Relationships,
    Segments,
    Superstars,
    TagTeams,
    TalentRequests
} from "../models/models.js";
import {
    addMatchChampionships,
    addMatchFeuds,
    getAllSuperstarsForMatch,
    getMatchChampionships,
    getMatchEvent,
    getMatchFeuds,
    getMatchGimmickMatch,
    getMatchParticipants,
    removeMatchChampionships,
    removeMatchFeud
} from "../models/match.js";
import {
    addSegmentFeuds,
    getSegmentEvent,
    getSegmentFeuds,
    getSegmentParticipants,
    getSegmentSegmentItem,
    removeSegmentFeud
} from "../models/segment.js";
import {getAlignmentBoost, getMatchData} from "./matches.js";
import {
    getSuperstarChampionshipReigns,
    getSuperstarChampionships,
    getSuperstarFactions,
    getSuperstarFeuds,
    getSuperstarRelationships,
    getSuperstarTagTeams,
    getSuperstarTalentRequests
} from "../models/superstar.js";
import {getTagTeamFeuds, getTagTeamSuperstars} from "../models/tagTeam.js";
import {getRosterActiveTagTeams, turnRosterMember} from "./rosters.js";
import {
    addCompanyGimmickMatches,
    addCompanyMessages,
    addCompanyNewsItems,
    getCompanyChampionships,
    getCompanyCompanyLogs,
    getCompanyFeudItems,
    getCompanyGimmickMatches,
    getCompanySegmentItems,
    getCompanyStaff
} from "../models/company.js";
import {
    addFactionSuperstars,
    getFactionFeuds,
    getFactionLeaderSuperstar,
    getFactionSuperstars,
    removeFactionSuperstars,
    setFactionLeaderSuperstar
} from "../models/faction.js";
import {addFeudItemFeuds, removeFeudItemFeuds} from "../models/feudItem.js";
import {getStreakTalent} from "../models/streak.js";
import {getChampionshipChampionshipReigns} from "../models/championship.js";
import {getChampionshipReignChampionship, getChampionshipReignSuperstar} from "../models/championshipReign.js";
import {checkSegmentItemRequirement, getSegmentData} from "./segments.js";
import {addGimmickMatchMatches, removeGimmickMatchMatches} from "../models/gimmickMatch.js";
import {checkAreChampionshipsEqual, getMatchAvailableChampionships, unifyChampionships} from "./championships.js";
import {checkTalentRequestAction} from "./talentRequests.js";
import {generateNewsItem} from "./news.js";
import {createTalentReleasedMessage} from "./messages.js";
import {removeTagTeam} from "./tagTeams.js";
import {updateMatchOrSegmentGraphics} from "./processMatch.js";
import {disbandFaction, processFactionXpChangeForFaction} from "./factions.js";
import {processPowSuperstarLeavesCompany} from "./passionOfWrestling.js";
import {checkToGoToNextTutorialState} from "./tutorial.js";

async function getFeudData(feud, getParticipants = false, month = null, year = null,
                           getHistory = false, getStaleLength = true, feudParticipants = null,
                           getUpdatedParticipants = false) {  // Previously get_feud_data
    let data = {};

    data['name'] = feud.name;
    data['hype'] = feud.hype;
    data['started'] = feud.started;
    data['runBlowoff'] = feud.runBlowoff;
    data['consequence'] = (await getFeudFeudItem(feud))?.consequence;
    data['ended'] = feud.ended;
    data['hot'] = feud.hot;
    data['stale'] = feud.stale;
    data['stalenessCounter'] = feud.stalenessCounter;
    data['uid'] = feud.$loki;

    // Get months since blowoff if feud ended
    if (feud.ended) {
        data['rating'] = feud.rating;
        if (month !== null) {
            data['monthsSinceBlowoff'] = getMonthsDifference(month, year, feud.endMonth, feud.endYear)
        }
    }

    // Get months elapsed
    if (data['started']) {
        if (!feud.ended) {
            if (month !== null) {
                data['monthsElapsed'] = getMonthsDifference(month, year, feud.startMonth, feud.startYear)
            }
        }
        else {
            data['monthsElapsed'] = getMonthsDifference(feud.endMonth, feud.endYear, feud.startMonth, feud.startYear)
        }
    }

    // Get participants
    if (getParticipants) {
        data['participants'] = await getFeudParticipantsNames(feud);
        if (getUpdatedParticipants) {
            data['participants'] = (await getFeudParticipants(feud)).map(x => x.name)
        }
    }

    // Get stale length
    if (getStaleLength) {
        if (!feudParticipants) {
            feudParticipants = await getFeudParticipants(feud);
        }
        data['staleLength'] = await getMatchesForStaleFeud(feud, feudParticipants)
    }

    if (getHistory) {

        // Get feud roster's superstars
        const feudRoster = await getFeudRoster(feud);
        let rosterSuperstars = [];
        if (feudRoster) {
            rosterSuperstars = await getRosterSuperstars(feudRoster)
        }

        // Get feud matches and segments in order of their events
        let feudMatchesAndSegments = (await getFeudMatches(feud)).concat((await getFeudSegments(feud)));
        const eventIds = await Promise.all(feudMatchesAndSegments.map(async x => (await getMatchEvent(x))?.$loki));
        feudMatchesAndSegments.sort((a, b) => {
            return eventIds[feudMatchesAndSegments.indexOf(b)] - eventIds[feudMatchesAndSegments.indexOf(a)]
        });

        // Add matches and segments data
        data['events'] = [];
        for (const y of feudMatchesAndSegments) {
            if (y.rating !== null) {
                let d;
                if (y.objectName === 'segment') {
                    d = await getSegmentData({segment: y, history: true, rosterSuperstars: rosterSuperstars});
                    d['eventType'] = 'segment';
                }
                else {
                    d = await getMatchData({match: y, history: true, getChampionshipsAvailable: false,
                        rosterSuperstars: rosterSuperstars});
                    d['eventType'] = 'match';
                }
                d['eventWeeklyShow'] = (await getSegmentEvent(y))?.weeklyShow;
                data['events'].push(d);
            }
        }

        // Get win records
        data['winRecords'] = data['participants'].map(x => {
            return {'name': x, 'wins': 0}
        });
        for (const x of data['winRecords']) {
            for (const m of data['events']) {
                if (m['eventType'] === 'match') {
                    // If trio/faction tagteam in match, use regex to search for trio/faction in match's winner name
                    if (m['type'] === 'tag' && m['participants'].some(y => y['trioTagTeam'] || y['factionTagTeam'])) {
                        if (findNameInString(x['name'], m['winner']['name'])) {
                            x['wins'] += 1
                        }
                    }
                    // Otherwise check if participant name is winner's name
                    else {
                        if (x['name'] === m['winner']['name']) {
                            x['wins'] += 1
                        }
                    }
                }
            }
        }

        // Get past feuds between same participants
        let pastFeuds = [];
        const feudParticipants = await getFeudParticipants(feud);
        const participantFeuds = feudParticipants.length > 0 ? (await getRosterMemberFeuds(feudParticipants[0])).reverse() : [];
        const participantPastFeuds = participantFeuds.filter(x => x.started && x.ended && x.$loki !== feud.$loki);
        for (const f of participantPastFeuds) {
            const feudParticipantNames = await getFeudParticipantsNames(f);
            if (feudParticipantNames.every(y => data['participants'].indexOf(y) >= 0)) {
                pastFeuds.push(f)
            }
        }
        data['pastFeuds'] = await Promise.all(pastFeuds.map(x => getFeudData(x, false, month, year)));
    }

    return data;
}

async function getFeudParticipantsNames(feud) {  // Previously get_feud_participant_names
    if (feud.participants) {
        return feud.participants
    }
    else {
        return (await getFeudParticipants(feud)).map(x => x.name)
    }
}

async function getMatchesForStaleFeud(feud, feudParticipants) {  // Previously get_matches_for_stale_feud
    let staleLength = 4;
    if (feud.hot) {
        staleLength = 6
    }

    // Get trait boost for feud staleness limit
    let traitBoost = 0;
    for (const participant of feudParticipants) {
        const participantSuperstars = await getRosterMemberSuperstars(participant);
        for (const superstar of participantSuperstars) {
            for (const trait of superstar.traits) {
                const traitData = traitsData[trait];
                if (traitData && traitData['feud_staleness_limit_boost']) {
                    traitBoost += traitData['feud_staleness_limit_boost']
                }
            }
        }
    }
    traitBoost = limitValueBetweenRange(traitBoost, -2, 2);
    staleLength += traitBoost;

    return staleLength;
}

async function getViewFeudsData() {  // Previously get_feuds_all
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');
    const rosterFeuds = (await getRosterFeuds(mainRoster)).reverse();
    let currentFeuds = [];
    let potentialFeuds = [];
    let pastFeuds = [];

    for (const [index, feud] of rosterFeuds.entries()) {
        const feudData = await getFeudData(feud, true, currentGame.monthCount, currentGame.year,
            false, false);
        feudData['order'] = index;  // Add order to feuds for sorting by date
        if (feud.started && feud.ended) {
            pastFeuds.push(feudData)
        }
        else if (feud.started) {
            currentFeuds.push(feudData)
        }
        else if (feud.directFeudEvent && !feud.ended) {
            potentialFeuds.push(feudData)
        }
    }

    data['currentFeuds'] = currentFeuds;
    data['potentialFeuds'] = potentialFeuds.slice(0, 8);
    data['pastFeuds'] = pastFeuds;

    return data;
}

async function getFeudHistoryData(uid) {  // Previously get_feud_history
    const currentGame = await getCurrentGame();
    const feud = await Feuds.get(uid);
    return getFeudData(feud, true, currentGame.monthCount, currentGame.year, true)
}

async function getRosterMemberFeuds(rosterMember) {
    if (rosterMember.objectName === 'superstar') {
        return getSuperstarFeuds(rosterMember)
    }
    else if (rosterMember.objectName === 'tagTeam') {
        return getTagTeamFeuds(rosterMember)
    }
    else {
        return getFactionFeuds(rosterMember)
    }
}

async function endViewFeudsFeud(uid) {  // Previously end_feud
    const currentGame = await getCurrentGame();
    const feud = await Feuds.get(uid);
    await endFeud(feud, currentGame.monthCount, currentGame.year);
    db.saveDatabase();
    return getFeudData(feud, true, currentGame.monthCount, currentGame.year);
}

async function endFeud(feud, month, year) {
    feud.ended = true;
    feud.endMonth = month;
    feud.endYear = year;

    // Get feud rating
    if (!feud.started) {
        feud.rating = null
    }
    else {
        feud.rating = await getFeudRating(feud)
    }

    return feud;
}

async function getFeudRating(feud) {  // Previously get_feud_rating
    const feudSegments = await getFeudSegments(feud);
    const feudMatches = await getFeudMatches(feud);
    const feudMatchesAndSegments = feudSegments.concat(feudMatches);
    return calculateFeudRating(feudMatchesAndSegments);
}

function calculateFeudRating(matchesAndSegments) {
    let filteredMatchesAndSegments = matchesAndSegments.filter(x => x.rating !== null);
    if (filteredMatchesAndSegments.length <= 0) {
        return 0
    }
    const ratingsSum = filteredMatchesAndSegments.map(x => x.rating).reduce((partialSum, a) => partialSum + a, 0);
    const average = ratingsSum / filteredMatchesAndSegments.length;
    return myround(average, 0.25);
}

async function getAllTalentData(participantType) {  // get_create_feud_participants
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');

    let participants = [];
    if (participantType === 'singles') {
        participants = await getRosterSuperstars(mainRoster)
    }
    else if (participantType === 'tag') {
        participants = await getRosterActiveTagTeams(mainRoster)
    }
    else if (participantType === 'trio') {
        participants = (await getRosterActiveTagTeams(mainRoster)).filter(x => x.trio)
    }
    else if (participantType === 'faction') {
        participants = (await getRosterFactions(mainRoster)).filter(x => !x.disbanded)
    }

    participants.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    return participants.map(x => {
        return {'name': x.name, 'uid': x.$loki}
    });
}

async function getCreateFeudItemsData(participantUids, participantType, participants) {  // get_create_feud_items
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const feudItems = (await getCompanyFeudItems(userCompany)).reverse();

    // Get feud participants
    if (!participants) {
        const mainRoster = await getCompanyRosterByType(userCompany, 'main');
        participants = [];
        for (const participantUid of participantUids) {
            let participant = await getFeudParticipantFromUid(participantUid, participantType, mainRoster);
            if (!participant) {
                data['error'] = 'participant-not-found';
                return data;
            }
            participants.push(participant);
        }
    }

    // Get available and unavailable feud items
    let unavailableFeudItems = [];
    let availableFeudItems = [];
    if (participants.length > 0) {
        let feudParticipantsType;
        if (participantType === 'trio') {
            feudParticipantsType = 'tag'
        }
        else {
            feudParticipantsType = participantType
        }
        const companyLogs = await getCompanyCompanyLogs(userCompany);
        const feudItemsData = await getAvailableFeudItems(feudItems, participants, userCompany.storyName,
            currentGame.monthCount, currentGame.year, companyLogs, feudParticipantsType);
        availableFeudItems = feudItemsData[0];
        unavailableFeudItems = feudItemsData[1];
    }
    else {
        availableFeudItems = feudItems;
    }

    // Get data for feud items
    availableFeudItems.sort((a, b) => a.baseHype - b.baseHype || a.requirement !== null > b.requirement !== null);
    unavailableFeudItems.sort((a, b) => a.baseHype - b.baseHype || a.requirement !== null > b.requirement !== null);
    data['availableFeudItems'] = availableFeudItems.map(x => getFeudItemData(x));
    data['unavailableFeudItems'] = unavailableFeudItems.map(x => getFeudItemData(x));

    // Get random feud name
    data['randomFeudName'] = generateRandomFeudItemName();

    return data;
}

async function getAvailableFeudsForMatch({matchUid, matchType, isSegment} = {}) {  // get_available_feuds_for_match
    let data = {};

    // Get participants
    let participants;
    if (isSegment) {
        let segment = await Segments.get(matchUid);
        if (!segment) {
            data['error'] = 'segment-not-found';
            return data;
        }
        participants = await getSegmentParticipants(segment);
    }
    else {
        let match = await Matches.get(matchUid);
        if (!match) {
            data['error'] = 'match-not-found';
            return data;
        }
        participants = await getMatchParticipants(match);
    }

    // Check if superstar vs team feud
    if (participants.some(x => x.objectName === 'superstar') && participants.some(x => x.objectName === 'tagTeam')) {
        data['error'] = 'superstar-and-team';
        return data;
    }
    if (matchType === 'tag') {
        // Set 'faction' matchType
        if (participants.every(x => x.factionTagTeam)) {
            matchType = 'faction'
        }
        // Check for faction vs non faction
        if (participants.some(x => x.factionTagTeam) && participants.some(x => !x.factionTagTeam)) {
            data['error'] = 'faction-vs-non-faction';
            return data;
        }
        // Check for temporary team
        if (participants.some(x => x.temporary && !x.factionTagTeam && !x.trioTagTeam)) {
            data['error'] = 'temporary-team';
            return data;
        }
    }

    // Get feud items
    data = await getCreateFeudItemsData([], matchType, participants);

    return data;
}

async function getAvailableFeudItems(feudItems, participants, storyName, month, year, companyLogs, participantsType) {  // get_available_feud_items
    let singles = false;
    let tag = false;
    let trio = false;
    const participantsSuperstars = (await Promise.all(participants.map(x => getRosterMemberSuperstars(x)))).flat();
    const retirementAge = (storyName !== 'mars') ? 45 : 55;
    const titleFeudRequirements = ['new-title-challenger', 'champions', 'long-title-reign', 'previous-title-feud',
            'previous-champion', 'old-and-champion'];

    // Filter feud items based on participants type
    if (participantsType === 'singles') {
        feudItems = feudItems.filter(x => x.singles);
        singles = true;
    }
    else if (participantsType === 'tag') {
        feudItems = feudItems.filter(x => x.tag);
        tag = true;
        trio = participants.every(x => x.trio);
    }
    else if (participantsType === 'faction') {
        feudItems = feudItems.filter(x => x.faction);
    }

    // Get feud items with no requirements
    let availableFeudItems = feudItems.filter(x => !x.requirement && x.unlocked);

    // Define check functions
    async function faceVsFaceCheck() {
        return participantsSuperstars.every(x => x.alignment === 'face');
    }
    async function heelVsHeelCheck() {
        return participantsSuperstars.every(x => x.alignment === 'heel');
    }
    async function faceVsHeelCheck() {
        if (singles) {
            return participantsSuperstars.some(x => x.alignment === 'face') &&
                participantsSuperstars.some(x => x.alignment === 'heel');
        }
        else {
            let heelsPresent = false;
            let facesPresent = false;
            for (const p of participants) {
                const ps = await getRosterMemberSuperstars(p);
                if (ps.every(x => x.alignment === 'face')) {
                    facesPresent = true
                }
                if (ps.every(x => x.alignment === 'heel')) {
                    heelsPresent = true
                }
            }
            return facesPresent && heelsPresent;
        }
    }
    async function partTimerVsFullTimerCheck() {
        return participantsSuperstars.some(x => x.partTimer) &&
            participantsSuperstars.some(x => !x.partTimer && !x.retired);
    }
    async function hallOfFamerReturnCheck() {
        return participantsSuperstars.some(x => x.hallOfFamer) &&
            participantsSuperstars.some(x => !x.partTimer && !x.retired && !x.hallOfFamer);
    }
    async function retiringSuperstarCheck() {
        return participantsSuperstars.some(x => x.age >= retirementAge);
    }
    async function retiringSuperstarsCheck() {
        return participantsSuperstars.every(x => x.age >= retirementAge);
    }
    async function facesOfTheCompanyCheck() {
        return participantsSuperstars.every(x => x.faceOfTheCompany);
    }
    async function becomingFotcCheck() {
        return participantsSuperstars.every(x => x.faceOfTheCompanyCounter > 0 && !x.faceOfTheCompany);
    }
    async function upperMidcardersCheck() {
        return participantsSuperstars.every(x => x.popularity >= 740 && x.popularity < 800);
    }
    async function lowerVsUpperMidcardersCheck() {
        return participantsSuperstars.some(x => getRankFromPopularity(x.popularity) === 'Lower Midcarder') &&
            participantsSuperstars.some(x => getRankFromPopularity(x.popularity) === 'Upper Midcarder');
    }
    async function unhappyVsHappyCheck() {
        return participantsSuperstars.some(x => !x.unhappy) && participantsSuperstars.some(x => x.unhappy);
    }
    async function youngVsOldCheck() {
        return participantsSuperstars.some(x => x.age > 37) && participantsSuperstars.some(x => x.age < 25);
    }
    async function youngMidcardersCheck() {
        return participantsSuperstars.every(x =>
            ['Upper Midcarder', 'Lower Midcarder'].indexOf(getRankFromPopularity(x.popularity)) >= 0 && x.age < 30);
    }
    async function highWorkrateCheck() {
        return participantsSuperstars.every(x => x.workrate >= 3);
    }
    async function faceVsFaceHotCheck() {
        return participantsSuperstars.every(x => x.alignment === 'face' && x.hot);
    }
    async function heelVsHeelHotCheck() {
        return participantsSuperstars.every(x => x.alignment === 'heel' && x.hot);
    }
    async function largePopularityLossCheck() {
        const ranks = {'Main Eventer': 4, 'Upper Midcarder': 3, 'Lower Midcarder': 2, 'Jabroni': 1};
        return participantsSuperstars.some(x => (ranks[getRankFromPopularity(x.popularity)] || 1) < (ranks[x.highestRank] || 1)) &&
            participantsSuperstars.some(x => (ranks[getRankFromPopularity(x.popularity)] || 1) >= (ranks[x.highestRank] || 1));
    }
    async function sameManagerCheck() {
        return participantsSuperstars.every(x => x.managerId && x.managerId === participantsSuperstars[0].managerId);
    }
    async function tagPartnersCheck() {
        const participantTagTeams = (await getSuperstarTagTeams(participantsSuperstars[0])).filter(x => !x.trio && !x.temporary);
        const participantNames = participants.map(x => x.name);
        for (const participantTagTeam of participantTagTeams) {
            const tagTeamMembers = await getTagTeamSuperstars(participantTagTeam);
            if (tagTeamMembers.map(x => x.name).every(y => participantNames.indexOf(y) >= 0)) {
                return true;
            }
        }
        return false;
    }
    async function differentBrandsCheck() {
        let brandsPresent = participantsSuperstars.filter(x => x.brandName).map(y => y.brandName);
        brandsPresent = [...new Set(brandsPresent)];
        return brandsPresent.length > 1;
    }
    async function championsCheck() {
        let notChampion = false;
        if (singles) {
            for (const participant of participants) {
                let participantChampionships = await getSuperstarChampionships(participant);
                participantChampionships = participantChampionships.filter(x => !x.tagTeam &&
                    !x.trio && !x.championshipContract && !x.matchContract);
                if (participantChampionships.length <= 0) {
                    notChampion = true;
                    break;
                }
            }
        }
        else if (tag) {
            for (const participant of participants) {
                const teamSuperstars = await getTagTeamSuperstars(participant);
                let participantChampionships = (await Promise.all(
                    teamSuperstars.map(x => getSuperstarChampionships(x)))
                ).flat();
                if (participant.trio) {
                    participantChampionships = participantChampionships.filter(x => x.trio &&
                    !x.championshipContract && !x.matchContract);
                }
                else {
                    participantChampionships = participantChampionships.filter(x => x.tagTeam &&
                    !x.championshipContract && !x.matchContract);
                }
                if (participantChampionships.length <= 0) {
                    notChampion = true;
                    break;
                }
            }
        }
        return !notChampion;
    }
    async function oldAndLongStreakCheck() {
        // Streak of 5 years or longer
        let streaks = (await Promise.all(
            participants.map(x => getRosterMemberStreaks(x)))
        ).flat().filter(x => x.length > 4);
        let challengerCheck = false;
        for (const streak of streaks) {
            const streakTalent = await getStreakTalent(streak);
            const streakChallenger = participants.find(x => x.$loki !== streakTalent.$loki);
            if (streakChallenger && streakChallenger.age >= 38) {
                challengerCheck = true;
            }
        }
        return challengerCheck;
    }
    async function oldAndChampionCheck() {
        let champions = [];
        for (const participant of participants) {
            let participantChampionships = await getSuperstarChampionships(participant);
            participantChampionships = participantChampionships.filter(x => !x.tagTeam &&
                !x.trio && !x.championshipContract && !x.matchContract);
            if (participantChampionships.length > 0) {
                champions.push(participant)
            }
        }
        let challengerCheck = false;
        if (champions.length === 1) {
            let champion = champions[0];
            const challenger = participantsSuperstars.find(x => x.$loki !== champion.$loki);
            if (challenger && challenger.age >= 38) {
                challengerCheck = true;
            }
        }
        return challengerCheck;
    }
    async function longStreakCheck() {
        // Streak of 5 years or longer
        let streaks = (await Promise.all(
            participants.map(x => getRosterMemberStreaks(x)))
        ).flat().filter(x => x.length > 4);
        return streaks.length > 0;
    }
    async function newTagTeamCheck() {
        const newTagTeamsNames = [];
        for (const companyLog of companyLogs) {
            if (companyLog.realYear !== null) {
                // Team created less than 6 months ago
                const monthsPassed = getMonthsDifference(month, year, companyLog.month, companyLog.realYear);
                if (companyLog.type === 'new-tag-team' && monthsPassed < 6) {
                    newTagTeamsNames.push(companyLog.text)
                }
            }
        }
        // One team is new, one is not
        return participants.some(x => newTagTeamsNames.indexOf(x.name) >= 0) &&
            participants.some(x => newTagTeamsNames.indexOf(x.name) < 0);
    }
    async function oldAndMaskCheck() {
        if (singles) {
            const maskedParticipants = participants.filter(x => checkSuperstarGraphicMask(x.graphic));
            if (maskedParticipants.length === 1) {
                const challenger = participants.find(x => x.$loki !== maskedParticipants[0].$loki);
                if (challenger && challenger.age >= 38) {
                    return true;
                }
            }
        }
        return false;
    }
    async function indieSuperstarCheck() {
        const signingNames = [];
        for (const companyLog of companyLogs) {
            if (companyLog.realYear !== null) {
                const monthsPassed = getMonthsDifference(month, year, companyLog.month, companyLog.realYear);
                if (companyLog.type === 'signing' && monthsPassed < 6) {
                    signingNames.push(companyLog.text)
                }
            }
        }
        return participants.some(x => signingNames.indexOf(x.name) >= 0) &&
            participants.some(x => signingNames.indexOf(x.name) < 0);
    }
    async function tradeDealCheck() {
        return participantsSuperstars.some(x => x.activeEffect === 'traded') &&
            participantsSuperstars.some(x => x.activeEffect !== 'traded');
    }
    async function previousChampionCheck() {
        let previousChampionPresent = false;
        for (const participant of participants) {
            // Find previous champion within reigns of champion
            let participantChampionships = await getSuperstarChampionships(participant);
            participantChampionships = participantChampionships.filter(x => !x.tagTeam &&
                !x.trio && !x.championshipContract && !x.matchContract);
            for (const participantChampionship of participantChampionships) {
                for (const reign of (await getChampionshipChampionshipReigns(participantChampionship))) {
                    const reignSuperstar = await getChampionshipReignSuperstar(reign);
                    if (objectInArray(reignSuperstar, participants) &&
                        reignSuperstar.$loki !== participant.$loki && reign.endMonth !== null) {
                        previousChampionPresent = true;
                    }
                }
            }
        }
        return previousChampionPresent;
    }
    async function factionVsFactionHotCheck() {
        const allFactionSuperstars = (await Promise.all(participants.map(x => getRosterMemberSuperstars(x)))).flat();
        return allFactionSuperstars.every(x => x.hot);
    }
    async function factionMembersCheck() {
        const faction = (await getSuperstarFactions(participants[0])).find(x => !x.disbanded);
        if (faction) {
            const factionSuperstars = await getFactionSuperstars(faction);
            // Check if faction superstars are above 2 and feud participants are present in faction
            if (factionSuperstars.length > 2 && participants.every(x => objectInArray(x, factionSuperstars))) {
                return true;
            }
        }
        return false;
    }
    async function factionLeaderPresentCheck() {
        const faction = (await getSuperstarFactions(participants[0])).find(x => !x.disbanded);
        if (faction) {
            const factionSuperstars = await getFactionSuperstars(faction);
            const leaderSuperstar = await getFactionLeaderSuperstar(faction);
            // Check if faction leader present and feud participants are present in faction
            if (leaderSuperstar && objectInArray(leaderSuperstar, factionSuperstars) &&
                participants.every(x => objectInArray(x, factionSuperstars))) {
                return true;
            }
        }
        return false;
    }
    async function noFactionLeaderCheck() {
        const faction = (await getSuperstarFactions(participants[0])).find(x => !x.disbanded);
        if (faction) {
            const factionSuperstars = await getFactionSuperstars(faction);
            const leaderSuperstar = await getFactionLeaderSuperstar(faction);
            // Check if faction leader not present and feud participants are present in faction
            if (!leaderSuperstar && participants.every(x => objectInArray(x, factionSuperstars))) {
                return true;
            }
        }
        return false;
    }
    async function factionPresentCheck() {
        let factionCheck = false;
        for (const p of participants) {
            const faction = (await getSuperstarFactions(p)).find(x => !x.disbanded);
            if (faction) {
                const factionSuperstars = await getFactionSuperstars(faction);
                // Check if faction superstars are below maximum and feud participants are not in same faction
                if (factionSuperstars.length < gameLimits.maxFactionSuperstars &&
                    !participants.every(x => objectInArray(x, factionSuperstars))) {
                    factionCheck = true;
                }
            }
        }
        return factionCheck;
    }
    async function matchContractWonCheck() {
        let matchContractFound = false;
        if (singles) {
            for (const participant of participants) {
                if ((await getSuperstarChampionships(participant)).find(x => x.matchContract)) {
                    matchContractFound = true;
                    break;
                }
            }
        }
        else if (tag) {
            for (const participant of participants) {
                const teamSuperstars = await getTagTeamSuperstars(participant);
                let participantChampionships = (await Promise.all(
                    teamSuperstars.map(x => getSuperstarChampionships(x)))
                ).flat();
                if (participant.trio) {
                    participantChampionships = participantChampionships.filter(x => x.trio && x.matchContract);
                }
                else {
                    participantChampionships = participantChampionships.filter(x => x.tagTeam && x.matchContract);
                }
                if (participantChampionships.length > 0) {
                    matchContractFound = true;
                    break;
                }
            }
        }
        return matchContractFound;
    }
    async function longTitleReignCheck() {
        if (singles) {
            let reignCheck = false;
            for (const participant of participants) {
                let participantReigns = await getSuperstarChampionshipReigns(participant);
                for (const reign of participantReigns) {
                    const reignChampionship = await getChampionshipReignChampionship(reign);
                    if (reign.endMonth === null &&
                        getMonthsDifference(month, year, reign.startMonth, reign.startYear) > 6 &&
                        reignChampionship && !reignChampionship.tagTeam && !reignChampionship.trio &&
                        !reignChampionship.vacated) {
                        reignCheck = true;
                    }
                }
            }
            return reignCheck;
        }
        else if (tag) {
            let reigns = [];
            for (const participant of participants) {
                const teamSuperstars = await getTagTeamSuperstars(participant);
                for (const s of teamSuperstars) {
                    let superstarReigns = await getSuperstarChampionshipReigns(s);
                    for (const reign of superstarReigns) {
                        const reignChampionship = await getChampionshipReignChampionship(reign);
                        if (reign.endMonth === null &&
                            getMonthsDifference(month, year, reign.startMonth, reign.startYear) > 6 &&
                            reignChampionship &&
                            (trio && reignChampionship.trio || !trio && reignChampionship.tagTeam) &&
                            !reignChampionship.vacated) {
                            reigns.push(reign);
                        }
                    }
                }
            }
            return reigns.length > 1;
        }
        return false;
    }
    async function previousHotFeudCheck() {
        let previousHotFeudCheck = false;
        for (const participant of participants) {
            const feuds = (await getRosterMemberFeuds(participant)).filter(x => x.started && x.ended && x.hot);
            for (const feud of feuds) {
                if ((await getFeudParticipants(feud)).every(x => objectInArray(x, participants))) {
                    previousHotFeudCheck = true;
                }
            }
        }
        return previousHotFeudCheck;
    }
    async function previousFeudsCheck() {
        let previousFeuds = [];
        for (const participant of participants) {
            const feuds = (await getRosterMemberFeuds(participant)).filter(x => x.started && x.ended && x.rating > 3.5);
            for (const feud of feuds) {
                if ((await getFeudParticipants(feud)).every(x => objectInArray(x, participants)) &&
                    !objectInArray(feud, previousFeuds)) {
                    previousFeuds.push(feud)
                }
            }
        }
        return previousFeuds.length >= 2;
    }
    async function newTitleChallengerCheck() {

        // Check for previous title feuds
        let previousTitleFeuds = [];
        for (const participant of participants) {
            let feuds = (await getRosterMemberFeuds(participant)).filter(x => x.started && x.ended);
            for (const feud of feuds) {
                const fi = await getFeudFeudItem(feud);
                if (titleFeudRequirements.indexOf(fi.requirement) >= 0 &&
                    (await getFeudParticipants(feud)).every(x => objectInArray(x, participants)) &&
                    !objectInArray(feud, previousTitleFeuds)) {
                    previousTitleFeuds.push(feud)
                }
            }
        }
        if (previousTitleFeuds.length >= 1) {
            return false;
        }

        // Check for champion
        let championCheck = false;
        if (singles) {
            for (const participant of participants) {
                let participantChampionships = await getSuperstarChampionships(participant);
                participantChampionships = participantChampionships.filter(x => !x.tagTeam &&
                    !x.trio && !x.championshipContract && !x.matchContract);
                if (participantChampionships.length > 0) {
                    championCheck = true;
                    break;
                }
            }
        }
        else if (tag) {
            for (const participant of participants) {
                const teamSuperstars = await getTagTeamSuperstars(participant);
                let participantChampionships = (await Promise.all(
                    teamSuperstars.map(x => getSuperstarChampionships(x)))
                ).flat();
                if (participant.trio) {
                    participantChampionships = participantChampionships.filter(x => x.trio &&
                    !x.championshipContract && !x.matchContract);
                }
                else {
                    participantChampionships = participantChampionships.filter(x => x.tagTeam &&
                    !x.championshipContract && !x.matchContract);
                }
                if (participantChampionships.length > 0) {
                    championCheck = true;
                    break;
                }
            }
        }
        return championCheck;
    }
    async function previousTitleFeudCheck() {

        // Check for previous title feuds
        let previousTitleFeuds = [];
        for (const participant of participants) {
            let feuds = (await getRosterMemberFeuds(participant)).filter(x => x.started && x.ended);
            for (const feud of feuds) {
                const fi = await getFeudFeudItem(feud);
                if (titleFeudRequirements.indexOf(fi.requirement) >= 0 &&
                    (await getFeudParticipants(feud)).every(x => objectInArray(x, participants)) &&
                    !objectInArray(feud, previousTitleFeuds)) {
                    previousTitleFeuds.push(feud)
                }
            }
        }
        if (previousTitleFeuds.length >= 1) {
            // Check if champion present, don't allow champion vs champion
            let champions = [];
            if (singles) {
                for (const participant of participants) {
                    let participantChampionships = await getSuperstarChampionships(participant);
                    participantChampionships = participantChampionships.filter(x => !x.tagTeam &&
                        !x.trio && !x.championshipContract && !x.matchContract);
                    if (participantChampionships.length > 0) {
                        champions.push(participant)
                    }
                }
            }
            else if (tag) {
                for (const participant of participants) {
                    const teamSuperstars = await getTagTeamSuperstars(participant);
                    let participantChampionships = (await Promise.all(
                        teamSuperstars.map(x => getSuperstarChampionships(x)))
                    ).flat();
                    if (participant.trio) {
                        participantChampionships = participantChampionships.filter(x => x.trio &&
                        !x.championshipContract && !x.matchContract);
                    }
                    else {
                        participantChampionships = participantChampionships.filter(x => x.tagTeam &&
                        !x.championshipContract && !x.matchContract);
                    }
                    if (participantChampionships.length > 0) {
                        champions.push(participant)
                    }
                }
            }
            return champions.length === 1;
        }
        return false;
    }
    const checkFunctions = {
        'face-vs-face': faceVsFaceCheck,
        'heel-vs-heel': heelVsHeelCheck,
        'part-timer-vs-full-timer': partTimerVsFullTimerCheck,
        'retiring-superstar': retiringSuperstarCheck,
        'retiring-superstars': retiringSuperstarsCheck,
        'faces-of-the-company': facesOfTheCompanyCheck,
        'becoming-fotc': becomingFotcCheck,
        'upper-midcarders': upperMidcardersCheck,
        'lower-vs-upper-midcarder': lowerVsUpperMidcardersCheck,
        'unhappy-vs-happy': unhappyVsHappyCheck,
        'young-vs-old': youngVsOldCheck,
        'young-midcarders': youngMidcardersCheck,
        'face-vs-heel': faceVsHeelCheck,
        'high-workrate': highWorkrateCheck,
        'face-vs-face-hot': faceVsFaceHotCheck,
        'heel-vs-heel-hot': heelVsHeelHotCheck,
        'large-popularity-loss': largePopularityLossCheck,
        'same-manager': sameManagerCheck,
        'tag-partners': tagPartnersCheck,
        'different-brands': differentBrandsCheck,
        'champions': championsCheck,
        'old-and-long-streak': oldAndLongStreakCheck,
        'old-and-champion': oldAndChampionCheck,
        'long-streak': longStreakCheck,
        'new-tag-team': newTagTeamCheck,
        'old-and-mask': oldAndMaskCheck,
        'indie-superstar': indieSuperstarCheck,
        'hall-of-famer-return': hallOfFamerReturnCheck,
        'trade-deal': tradeDealCheck,
        'previous-champion': previousChampionCheck,
        'faction-vs-faction-hot': factionVsFactionHotCheck,
        'faction-members': factionMembersCheck,
        'faction-leader-present': factionLeaderPresentCheck,
        'no-faction-leader': noFactionLeaderCheck,
        'faction-present': factionPresentCheck,
        'match-contract-won': matchContractWonCheck,
        'long-title-reign': longTitleReignCheck,
        'new-title-challenger': newTitleChallengerCheck,
        'previous-feuds': previousFeudsCheck,
        'previous-title-feud': previousTitleFeudCheck,
        'previous-hot-feud': previousHotFeudCheck,
    };

    // Check feud items with requirements
    let feudItemsToCheck = feudItems.filter(x => x.requirement && x.unlocked);
    for (const feudItem of feudItemsToCheck) {
        const checkFunction = checkFunctions[feudItem.requirement];
        if (checkFunction && (await checkFunction())) {
            availableFeudItems.push(feudItem)
        }
    }

    // Add direct feuds
    for (const participant of participants) {
        for (const feud of (await getRosterMemberFeuds(participant))) {
            if (!feud.started && feud.directFeudEvent && !feud.ended &&
                (await getFeudParticipants(feud)).every(x => objectInArray(x, participants))) {
                const fi = feudItemsToCheck.filter(x => x.requirement === feud.directFeudEvent);
                for (const y of fi) {
                    if (!objectInArray(y, availableFeudItems)) {
                        availableFeudItems.push(y)
                    }
                }
            }
        }
    }

    // Get unavailable feud items
    let availableFeudItemIds = availableFeudItems.map(x => x.$loki);
    let unavailableFeudItems = feudItems.filter(x => availableFeudItemIds.indexOf(x.$loki) < 0);

    return [availableFeudItems, unavailableFeudItems];
}

function getFeudItemData(feudItem) {  // get_feud_item_data
    let data = {};
    data['name'] = feudItem.name;
    data['description'] = feudItem.description;
    data['level'] = feudItem.level;
    data['xp'] = feudItem.xp;
    data['baseHype'] = feudItem.baseHype;
    data['consequence'] = feudItem.consequence;
    data['requirement'] = feudItem.requirement;
    data['oneAtATime'] = feudItem.oneAtATime;
    data['category'] = feudItem.category;
    data['unlocked'] = feudItem.unlocked;
    data['uid'] = feudItem.$loki;
    return data;
}

function generateRandomFeudItemName(previousName) {  // generate_feud_item_name
    let names = ['Spite', 'Marital Dispute', 'You Took My Banana', 'True Opposites', 'Stalker', 'Patriotism',
        'Kidnapping', 'Family Affair', 'Embarrassment', 'I\'m Above You', 'Financial Trouble',
        'My Hair Is Better', 'Love or Hate?', 'Demonic Exorcism', 'Insulted My Spouse', 'Jealousy', 'Resentment',
        'Mother Insult', 'Property Destruction', 'Vanity', 'Cheating Spouse', 'Illegitimate Child',
        'Hurt My Ego', 'Insulting The Dead', 'Backstage Heat', 'Stole My Friend', 'Better Entrance',
        'You Are A Prude', 'God Hates You', 'Moral Disagreement', 'Pineapple on Pizza', 'Stole My Gimmick',
        'Burned Down House', 'Goo Fight', 'Better Merch', 'Duel', 'That\'s My Move', 'Toilet Prank',
        'Former Friends', 'Creeper', 'You Smell', 'Undeserving', 'God Loves You', 'I Loved You', 'Better Cook',
        'Murder', 'Better Car', 'Last Rites', 'Stolen Parking Spot', 'Intimidation', 'Torment'];
    if (previousName) {
        names = names.filter(item => item !== previousName)
    }
    return getRandomChoice(names);
}

async function createFeud(feudItemUid, customFeudName, participantUids, participantType) {  // create_feud
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');
    const cost = 5;

    // Check if enough points
    if (userCompany.points < cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Get feud item
    const feudItem = await FeudItems.get(feudItemUid);
    if (!feudItem) {
        data['error'] = 'feud-item-not-found';
        return data;
    }

    // Get feud participants
    let feudParticipants = [];
    for (const participantUid of participantUids) {
        let participant = await getFeudParticipantFromUid(participantUid, participantType, mainRoster);
        if (!participant) {
            data['error'] = 'participant-not-found';
            return data;
        }
        if (feudParticipants.map(x => x.$loki).indexOf(participant.$loki) >= 0) {
            data['error'] = 'duplicate-participants';
            return data;
        }
        feudParticipants.push(participant);
    }

    // Check for existing feuds
    const feudParticipantNames = feudParticipants.map(x => x.name);
    for (const f of (await getRosterMemberFeuds(feudParticipants[0]))) {
        // Check running feuds with same participants
        if ((f.started && !f.ended) || (!f.started && f.directFeudEvent && !f.ended)) {
            if ((await getFeudParticipants(f)).every(x => feudParticipantNames.indexOf(x.name) >= 0)) {
                data['error'] = 'existing-feud-present';
                return data;
            }
        }
    }

    // Create feud
    const newFeud = Feud();
    newFeud.name = customFeudName || feudItem.name;
    newFeud.hype = feudItem.baseHype;
    newFeud.startMonth = currentGame.monthCount;
    newFeud.startYear = currentGame.year;
    newFeud.directFeudEvent = 'created-manually';
    newFeud.participants = feudParticipantNames;

    // Get who to apply consequence to
    if (feudItem.oneWayConsequence) {
        let feudParticipantsType;
        if (participantType === 'trio') {
            feudParticipantsType = 'tag'
        }
        else {
            feudParticipantsType = participantType
        }
        newFeud.consequenceAppliedTo = await getFeudConsequenceAppliedTo(feudItem, feudParticipants, feudParticipantsType);
    }

    // Save feud to db
    await Feuds.insert(newFeud);

    // Add feud to feud item and roster
    await addFeudItemFeuds(feudItem, [newFeud]);
    await addRosterFeuds(mainRoster, [newFeud]);

    // Add feud to participants
    if (participantType === 'singles') {
        await addFeudSuperstars(newFeud, feudParticipants)
    }
    else if (participantType === 'faction') {
        await addFeudFactions(newFeud, feudParticipants)
    }
    else {
        await addFeudTagTeams(newFeud, feudParticipants)
    }

    // Remove points
    userCompany.points -= cost;

    // Check tutorial action
    if (userCompany.storyName === 'tutorial') {
        data['nextTutorialState'] = await checkToGoToNextTutorialState(userCompany, ['add_feud']);
    }

    db.saveDatabase();

    // Get data to return
    data['feudName'] = newFeud.name;
    data['feudHype'] = newFeud.hype;
    data['feudParticipantNames'] = feudParticipantNames;
    data['consequenceAppliedTo'] = newFeud.consequenceAppliedTo;
    data['consequence'] = feudItem.consequence;
    data['points'] = userCompany.points;

    return data;
}

async function getFeudConsequenceAppliedTo(feudItem, feudParticipants, feudParticipantsType) {
    if (feudItem.consequence === 'retirement') {
        if (feudItem.requirement === 'retiring-superstar') {
            const consequenceParticipant = feudParticipants.reduce((prev, current) => (prev.age > current.age) ? prev : current);
            return consequenceParticipant.name;
        }
        else if (feudItem.requirement === 'old-and-long-streak') {
            // Streak of 5 years or longer
            let streaks = (await Promise.all(
                feudParticipants.map(x => getRosterMemberStreaks(x)))
            ).flat().filter(x => x.length > 4);
            for (const streak of streaks) {
                const streakTalent = await getStreakTalent(streak);
                const streakChallenger = feudParticipants.find(x => x.$loki !== streakTalent.$loki);
                if (streakChallenger && streakChallenger.age >= 38) {
                    return streakChallenger.name;
                }
            }
        }
        else if (feudItem.requirement === 'old-and-champion') {
            let champions = [];
            for (const participant of feudParticipants) {
                let participantChampionships = await getSuperstarChampionships(participant);
                participantChampionships = participantChampionships.filter(x => !x.tagTeam &&
                    !x.trio && !x.championshipContract && !x.matchContract);
                if (participantChampionships.length > 0) {
                    champions.push(participant)
                }
            }
            if (champions.length === 1) {
                let champion = champions[0];
                const challenger = feudParticipants.find(x => x.$loki !== champion.$loki);
                if (challenger && challenger.age >= 38) {
                    return challenger.name
                }
            }
        }
    }
    if (feudItem.consequence === 'unavailable') {
        if (feudItem.requirement === 'previous-title-feud') {
            if (feudParticipantsType === 'singles') {
                for (const participant of feudParticipants) {
                    let participantChampionships = await getSuperstarChampionships(participant);
                    participantChampionships = participantChampionships.filter(x => !x.tagTeam &&
                        !x.trio && !x.championshipContract && !x.matchContract);
                    if (participantChampionships.length <= 0) {
                        return participant.name;
                    }
                }
            }
            else if (feudParticipantsType === 'tag') {
                for (const participant of feudParticipants) {
                    const teamSuperstars = await getTagTeamSuperstars(participant);
                    let participantChampionships = (await Promise.all(
                        teamSuperstars.map(x => getSuperstarChampionships(x)))
                    ).flat();
                    if (participant.trio) {
                        participantChampionships = participantChampionships.filter(x => x.trio &&
                        !x.championshipContract && !x.matchContract);
                    }
                    else {
                        participantChampionships = participantChampionships.filter(x => x.tagTeam &&
                        !x.championshipContract && !x.matchContract);
                    }
                    if (participantChampionships.length <= 0) {
                        return participant.name;
                    }
                }
            }
        }
        if (feudItem.requirement === 'hall-of-famer-return') {
            const consequenceParticipant = feudParticipants.find(x => x.hallOfFamer);
            if (consequenceParticipant) {
                return consequenceParticipant.name
            }
        }
    }
    if (feudItem.consequence === 'winner-turns-face') {
        if (feudParticipantsType === 'singles') {
            const consequenceParticipant = feudParticipants.find(x => x.alignment === 'heel');
            if (consequenceParticipant) {
                return consequenceParticipant.name
            }
        }
        else if (['tag', 'faction'].indexOf(feudParticipantsType) >= 0) {
            for (const participant of feudParticipants) {
                const participantSuperstars = await getRosterMemberSuperstars(participant);
                if (participantSuperstars.every(x => x.alignment === 'heel')) {
                    return participant.name
                }
            }
        }
    }
    if (feudItem.consequence === 'loser-turns-heel') {
        if (feudParticipantsType === 'singles') {
            const consequenceParticipant = feudParticipants.find(x => x.alignment === 'face');
            if (consequenceParticipant) {
                return consequenceParticipant.name
            }
        }
        else if (['tag', 'faction'].indexOf(feudParticipantsType) >= 0) {
            for (const participant of feudParticipants) {
                const participantSuperstars = await getRosterMemberSuperstars(participant);
                if (participantSuperstars.every(x => x.alignment === 'face')) {
                    return participant.name
                }
            }
        }
    }

    return null;
}

async function getFeudsForBooking({matchType} = {}) {  // Previously _get_feuds
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const companyChampionships = await getCompanyChampionships(userCompany);
    const nextEvent = await getCompanyLatestEvent(userCompany);
    const rosterFeuds = await getRosterFeuds(mainRoster);
    let feuds = [];

    // Get feuds based on match type
    let matchTypeFeuds = [];
    for (const feud of rosterFeuds) {
        if (!feud.ended) {

            // Get feud participants, cache for later
            const feudParticipants = await getFeudParticipants(feud);
            feud.tempParticipants = feudParticipants;

            // Get participant type
            let participantType;
            if (feudParticipants.length > 0) {
                participantType = feudParticipants[0].objectName
            }

            if (matchType === 'singles') {
                if (participantType === 'superstar') {
                    matchTypeFeuds.push(feud)
                }
            }
            else if (matchType === 'tag') {
                if (participantType === 'tagTeam' || participantType === 'faction') {
                    matchTypeFeuds.push(feud)
                }
            }
            else if (matchType === 'trio') {
                // Only get feuds with all trio participants, and all faction participants having more than 2 superstars
                if (feudParticipants.every(x => x.objectName === 'tagTeam' && x.trio)) {
                    matchTypeFeuds.push(feud)
                }
                for (const p of feudParticipants) {
                    if (p.objectName === 'faction') {
                        p.tempSuperstars = await getFactionSuperstars(p)
                    }
                }
                if (feudParticipants.every(x => x.objectName === 'faction' && x.tempSuperstars.length > 2)) {
                    matchTypeFeuds.push(feud)
                }
                for (const p of feudParticipants) {
                    if (p.objectName === 'faction') {
                        delete p.tempSuperstars
                    }
                }
            }
            // Segment
            else {
                matchTypeFeuds.push(feud)
            }
        }
    }

    // Check feud participant availability
    for (const feud of matchTypeFeuds) {
        if (!objectInArray(feud, feuds)) {
            if (await checkFeudParticipantsAvailability(feud.tempParticipants, rosterSuperstars,
                matchType, nextEvent, companyChampionships)) {
                feuds.push(feud)
            }
        }
    }

    // Sort feuds by hype
    feuds.sort((a, b) => b.hype - a.hype);

    // Get data for feuds
    data['feuds'] = await Promise.all(feuds.map(x => getFeudData(x, true, currentGame.monthCount,
        currentGame.year, false, true, x.tempParticipants, true)));

    return data;
}

async function checkFeudParticipantsAvailability(participants, rosterSuperstars, matchType, nextEvent,
                                                 companyChampionships) {  // check_feud_participants_availability
    for (const x of participants) {
        if (x.objectName === 'superstar') {
            if (matchType === 'segment') {
                if (!checkSuperstarSegmentAvailability(x, false)) {
                    return false;
                }
            } else {
                if (!checkAvailability(x, true)) {
                    return false;
                }
            }

            if (!objectInArray(x, rosterSuperstars)) {
                return false;
            }

            // Check for brand exclusive event
            const superstarChampionships = getSuperstarChampionshipsFromCompanyChampionships(x, companyChampionships);
            if (!checkSuperstarBrandAvailability(nextEvent, x, superstarChampionships)) {
                return false;
            }
        }
        else if (x.objectName === 'tagTeam' || x.objectName === 'faction') {
            let minAvailableMembers = (matchType === 'trio') ? 3 : 2;
            if (matchType === 'segment') {
                minAvailableMembers = 2
            }

            // Check if minimum number of superstars are available
            const superstars = await getRosterMemberSuperstars(x);
            let availableSuperstars;
            if (matchType === 'segment') {
                availableSuperstars = superstars.filter(x => checkSuperstarSegmentAvailability(x, false))
            }
            else {
                availableSuperstars = superstars.filter(x => checkAvailability(x, true))
            }
            availableSuperstars = availableSuperstars.filter(x => checkSuperstarBrandAvailability(nextEvent, x,
                getSuperstarChampionshipsFromCompanyChampionships(x, companyChampionships))
                && objectInArray(x, rosterSuperstars));

            if (availableSuperstars.length < minAvailableMembers) {
                return false;
            }
        }
    }
    return true;
}

async function getFeudParticipantFromUid(uid, type, mainRoster) {
    let participant;
    if (type === 'singles') {
        participant = await Superstars.get(uid)
    }
    else if (type === 'faction') {
        participant = (await getRosterFactions(mainRoster)).find(x => x.$loki === uid && !x.disbanded)
    }
    else {
        participant = await TagTeams.get(uid)
    }
    return participant;
}

async function getFeudSegmentsInformation({uid} = {}) {  // get_feud_segments_information
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const segmentItems = await getCompanySegmentItems(userCompany);
    const companyLogs = await getCompanyCompanyLogs(userCompany);
    const companyChampionships = await getCompanyChampionships(userCompany);
    const staffMembers = await getCompanyStaff(userCompany);
    const availableStaffMembers = await getAvailableStaffMembers(staffMembers);

    // Get feud
    const feud = await Feuds.get(uid);

    // Get segment item names for past segments in feud, besides repeatable ones
    data['feudSegmentNames'] = [];
    for (const feudSegment of (await getFeudSegments(feud))) {
        const segmentItem = await getSegmentSegmentItem(feudSegment);
        if (feudSegment.rating && segmentItem.oneAtATime && !data['feudSegmentNames'].includes(segmentItem.name)) {
            data['feudSegmentNames'].push(segmentItem.name)
        }
    }

    // Get unavailable segment items
    const participants = await getFeudParticipants(feud);
    let unavailableSegmentItems = [];
    let requirementChecksCompleted = [];
    let requirementChecksFailed = [];

    for (const x of segmentItems) {
        // Check number of participants
        if (participants.length < x.minParticipants || participants.length > x.maxParticipants) {
            unavailableSegmentItems.push(x.name)
        }
        else {
            if (x.requirement) {
                // Don't check requirement if already checked
                if (requirementChecksCompleted.includes(x.requirement) || requirementChecksFailed.includes(x.requirement)) {
                    if (requirementChecksFailed.includes(x.requirement)) {
                        unavailableSegmentItems.push(x.name)
                    }
                }
                else {
                    let check = await checkSegmentItemRequirement(x, participants, companyLogs, currentGame.monthCount,
                        currentGame.year, companyChampionships, availableStaffMembers);
                    // Store requirement check
                    if (!check.error) {
                        requirementChecksCompleted.push(x.requirement)
                    }
                    else {
                        requirementChecksFailed.push(x.requirement);
                        unavailableSegmentItems.push(x.name)
                    }
                }
            }
        }
    }

    data['unavailableSegments'] = unavailableSegmentItems;

    return data;
}

function getPreviousFeudHypeBoost(previousFeud, month, year) {  // get_previous_feud_hype_boost
    let hype = previousFeud.hype;
    hype = limitValueBetweenRange(hype, hype, 80);
    let monthsPassed = getMonthsDifference(month, year, previousFeud.endMonth, previousFeud.endYear);
    monthsPassed = limitValueBetweenRange(monthsPassed, monthsPassed, 12);
    let hypeBoost = 0;

    // Add months passed factor
    if (monthsPassed <= 6) {
        hypeBoost = -90 + (15 * monthsPassed)
    }
    else {
        if (hype > 15) {
            hypeBoost += monthsPassed - 2
        }
    }

    // Add previous feud's rating factor
    hypeBoost += Math.floor((hype - 15) / 4);

    return hypeBoost;
}

async function getParticipantFeuds(superstars, teams) {  // get_participant_feuds
    let feuds = [];

    async function checkFeud(f, arr) {
        if ((f.started && !f.ended) || (!f.started && f.directFeudEvent && !f.ended)) {
            const p = await getFeudParticipants(f);
            if (p.every(x => objectInArray(x, arr))) {
                return true
            }
        }
        return false
    }

    let factions = [];
    for (const s of superstars) {
        let superstarFactions = await getSuperstarActiveFactions(s);
        factions.push(...superstarFactions);
        for (const feud of await getRosterMemberFeuds(s)) {
            if (await checkFeud(feud, superstars)) {
                feuds.push(feud)
            }
        }

    }

    for (const t of teams) {
        for (const feud of await getRosterMemberFeuds(t)) {
            if (await checkFeud(feud, teams)) {
                feuds.push(feud)
            }
        }
    }

    for (const f of getUniqueObjects(factions)) {
        for (const feud of await getRosterMemberFeuds(f)) {
            if (await checkFeud(feud, factions)) {
                feuds.push(feud)
            }
        }
    }

    return getUniqueObjects(feuds);
}

async function addFeudToMatchOrSegment({matchOrSegmentUid, matchType, isSegment, feudItemUid, feudName} = {}) {  // add_feud_to_match
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');

    // Get match and its participants
    let participants;
    let matchOrSegment;
    if (isSegment) {
        matchOrSegment = await Segments.get(matchOrSegmentUid);
        if (!matchOrSegment) {
            data['error'] = 'segment-not-found';
            return data;
        }
        participants = await getSegmentParticipants(matchOrSegment);
    }
    else {
        matchOrSegment = await Matches.get(matchOrSegmentUid);
        if (!matchOrSegment) {
            data['error'] = 'match-not-found';
            return data;
        }
        participants = await getMatchParticipants(matchOrSegment);
    }

    // Get feud item
    const feudItem = await FeudItems.get(feudItemUid);
    if (!feudItem) {
        data['error'] = 'feud-item-not-found';
        return data;
    }

    // If trio or faction temporary tag team present, add the trio or faction to participants
    if (matchType === 'tag') {
        let newParticipants = [];
        for (const p of participants) {
            if (p.trioTagTeam) {
                let participantSuperstars = await getRosterMemberSuperstars(p);
                let trio = (await getSuperstarTagTeams(participantSuperstars[0])).find(x => x.trio);
                if (trio) {
                    newParticipants.push(trio)
                }
            }
            else if (p.factionTagTeam) {
                let participantSuperstars = await getRosterMemberSuperstars(p);
                let faction = (await getSuperstarFactions(participantSuperstars[0])).find(x => !x.disbanded);
                if (faction) {
                    newParticipants.push(faction)
                }
            }
            else {
                newParticipants.push(p)
            }
        }
        participants = [...newParticipants];
    }

    // Get participant type
    let participantType = matchType;
    if (participants.some(x => x.objectName === 'faction')) {
        participantType = 'faction'
    }

    // Check for existing feuds
    let existingFeud;
    for (const f of (await getRosterMemberFeuds(participants[0]))) {
        // Check running feuds with same participants
        if ((f.started && !f.ended) || (!f.started && f.directFeudEvent && !f.ended)) {
            if ((await getFeudParticipants(f)).every(x => objectInArray(x, participants))) {
                existingFeud = f;
                break;
            }
        }
    }

    // Create new feud or use existing
    let newFeud;
    if (existingFeud) {
        newFeud = existingFeud;
        let existingFeudItem = await getFeudFeudItem(newFeud);
        await removeFeudItemFeuds(existingFeudItem, [newFeud]);
    }
    else {
        newFeud = Feud();
        newFeud.participants = participants.map(x => x.name);
    }

    // Add new feud attributes
    newFeud.name = feudName || feudItem.name;
    newFeud.hype = feudItem.baseHype;
    newFeud.startMonth = currentGame.monthCount;
    newFeud.startYear = currentGame.year;

    // Get who to apply consequence to
    if (feudItem.oneWayConsequence) {
        newFeud.consequenceAppliedTo = await getFeudConsequenceAppliedTo(feudItem, participants, matchType);
        data['consequenceAppliedTo'] = newFeud.consequenceAppliedTo;
    }

    // Save feud to db
    await Feuds.insert(newFeud);

    // Add feud to feud item and roster
    await addFeudItemFeuds(feudItem, [newFeud]);
    await addRosterFeuds(mainRoster, [newFeud]);

    // Add feud to match/segment
    if (isSegment) {
        await addSegmentFeuds(matchOrSegment, [newFeud])
    }
    else {
        await addMatchFeuds(matchOrSegment, [newFeud]);
    }

    // Add feud to participants
    if (!existingFeud) {
        if (participantType === 'singles') {
            await addFeudSuperstars(newFeud, participants)
        } else if (participantType === 'faction') {
            await addFeudFactions(newFeud, participants)
        } else {
            await addFeudTagTeams(newFeud, participants)
        }
    }

    // Check tutorial action
    if (userCompany.storyName === 'tutorial') {
        data['nextTutorialState'] = await checkToGoToNextTutorialState(userCompany, ['add_feud_to_match']);
    }

    db.saveDatabase();

    // Get data to return
    data['feud'] = await getFeudData(newFeud);
    data['previousFeudBoost'] = await getFeudCreationPreviousFeudsBoost(newFeud, participants, currentGame.monthCount,
        currentGame.year);

    return data;
}

async function getFeudCreationBoosts(feud, participants, month, year, style) {
    let data = {};
    let brandsPresent;
    data['rankBoost'] = await getRankBoost(participants);
    data['alignmentBoost'] = await getAlignmentBoost(participants, style);

    if (participants[0].objectName === 'superstar') {
        data['hotParticipantsBoost'] = participants.filter(x => x.hot).length * 10;
        data['staleParticipantsBoost'] = participants.filter(x => x.stale).length * -10;
        brandsPresent = [...new Set((participants.filter(x => x.brandName).map(x => x.brandName)))];
    } else if (['tagTeam', 'faction'].includes(participants[0].objectName)) {
        let superstars = (await Promise.all(participants.map(x => getRosterMemberSuperstars(x)))).flat();
        data['hotParticipantsBoost'] = superstars.filter(x => x.hot).length * 5;
        data['staleParticipantsBoost'] = superstars.filter(x => x.stale).length * -5;
        brandsPresent = [...new Set((superstars.filter(x => x.brandName).map(x => x.brandName)))];
    }

    // Get brands boost
    data['brandsBoost'] = 0;
    if (brandsPresent.length > 1) {
        data['brandsBoost'] = 10;
    }

    // Get previous feuds boost
    data['previousFeudBoost'] = await getFeudCreationPreviousFeudsBoost(feud, participants, month, year);

    // Add faction reputation boost if no previous feud penalty
    data['factionReputationBoost'] = 0;
    if (data['previousFeudBoost'] >= 0) {
        data['factionReputationBoost'] = participants.filter(x => x.objectName === 'faction')
            .reduce((acc, x) => acc + x.reputation * 3, 0);
    }

    data['hypeBoost'] = data['rankBoost'] + data['alignmentBoost'] + data['previousFeudBoost'] +
        data['hotParticipantsBoost'] + data['staleParticipantsBoost'] + data['factionReputationBoost'] +
        data['brandsBoost'];

    return data;
}

async function getRankBoost(participants) {  // get_rank_boost
    let ranks = {'Main Eventer': 4, 'Upper Midcarder': 2, 'Lower Midcarder': 1, 'Jabroni': 0};
    let rankBoost = 0;

    for (let p of participants) {
        if (p.objectName === 'superstar') {
            if (p.faceOfTheCompany) {
                rankBoost += 12;
            } else {
                rankBoost += ranks[getRankFromPopularity(p.popularity)] || 0;
            }
        } else if (['tagTeam', 'faction'].includes(p.objectName)) {
            for (let s of (await getRosterMemberSuperstars(p))) {
                if (s.faceOfTheCompany) {
                    rankBoost += 3;
                } else {
                    rankBoost += (ranks[getRankFromPopularity(s.popularity)] || 0) / 2;
                }
            }
        }
    }
    rankBoost /= 2;

    return Math.floor(rankBoost);
}

async function getFeudCreationPreviousFeudsBoost(feud, participants, month, year) {
    let previousFeudBoost = 0;
    for (const p of participants) {
        let previousFeuds = [];
        for (const f of (await getRosterMemberFeuds(p))) {
            if (f.started && f.ended && (await getFeudParticipants(f)).every(x => objectInArray(x, participants))
                && f.$loki !== feud.$loki) {
                previousFeuds.push(f)
            }
        }
        if (previousFeuds.length > 0) {
            previousFeuds.sort((a, b) => b.endYear - a.endYear || b.endMonth - a.endMonth);
            let previousFeud = previousFeuds[0];
            previousFeudBoost = getPreviousFeudHypeBoost(previousFeud, month, year);
            break;
        }
    }
    return previousFeudBoost;
}

async function removeFeudFromMatchOrSegment({matchOrSegmentUid, isSegment} = {}) {  // removeFeudFromMatchOrSegment
    let data = {};

    // Get match and its participants
    let feuds;
    let matchOrSegment;
    if (isSegment) {
        matchOrSegment = await Segments.get(matchOrSegmentUid);
        if (!matchOrSegment) {
            data['error'] = 'segment-not-found';
            return data;
        }
        feuds = await getSegmentFeuds(matchOrSegment);
    }
    else {
        matchOrSegment = await Matches.get(matchOrSegmentUid);
        if (!matchOrSegment) {
            data['error'] = 'match-not-found';
            return data;
        }
        feuds = await getMatchFeuds(matchOrSegment);
    }

    // Remove feuds
    data['deletedPotentialFeudUids'] = [];
    for (const feud of feuds) {
        // Delete potential feuds
        if (!feud.started && !feud.directFeudEvent) {
            await FeudParticipantsHash.findAndRemove({"feudId": feud.$loki});
            await FeudMatchesHash.findAndRemove({"feudId": feud.$loki});
            await FeudSegmentsHash.findAndRemove({"feudId": feud.$loki});
            await Feuds.remove(feud);
            data['deletedPotentialFeudUids'].push(feud.$loki);
        }
        else {
            // Remove direct feud from match/segment
            if (isSegment) {
                await removeSegmentFeud(matchOrSegment, feud)
            }
            else {
                await removeMatchFeud(matchOrSegment, feud)
            }
        }
    }

    db.saveDatabase();

    return data;
}

async function changeFeudBlowoff({matchUid, matchType, feudUid, add} = {}) {  // change_feud_blowoff
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const companyChampionships = await getCompanyChampionships(userCompany);

    // Get match
    let match = await Matches.get(matchUid);
    if (!match) {
        data['error'] = 'match-not-found';
        return data;
    }

    // Get feud
    let feud = await Feuds.get(feudUid);
    let feudParticipants = await getFeudParticipants(feud);
    let feudItem = await getFeudFeudItem(feud);

    // Check for 1 on 1 match if consequence
    if (feudItem.consequence) {
        let matchParticipants = await getMatchParticipants(match);
        if (matchParticipants.length > 2) {
            data['error'] = 'multi-person-match';
            return data;
        }

        if (add) {
            // Only allow blowoff if match type is same as feud participant type
            if (feudParticipants[0].objectName === 'superstar') {
                if (matchType !== 'singles') {
                    data['error'] = 'singles-match-blowoff';
                    return data;
                }
            }
            if (feudParticipants[0].objectName === 'faction') {
                if (matchType !== 'tag') {
                    data['error'] = 'faction-match-blowoff';
                    return data;
                }
            }
            if (feudParticipants[0].objectName === 'tagTeam') {
                if (matchType !== 'tag') {
                    data['error'] = 'tag-match-blowoff';
                    return data;
                }
                if (feudParticipants.every(x => x.trio)) {
                    // Allow trios feud blowoff in tag or trios match
                    for (const x of matchParticipants) {
                        let participantSuperstars = await getTagTeamSuperstars(x);
                        if (!([2, 3].includes(participantSuperstars.length))) {
                            data['error'] = 'trio-match-blowoff';
                            return data;
                        }
                    }
                }
                else {
                    // Allow tag feud blowoff in tag match only
                    for (const x of matchParticipants) {
                        let participantSuperstars = await getTagTeamSuperstars(x);
                        if (participantSuperstars.length !== 2) {
                            data['error'] = 'tag-match-blowoff';
                            return data;
                        }
                    }
                }
            }
        }
    }

    // Check if blowoff already set
    if (add && feud.runBlowoff) {
        data['error'] = 'blowoff-set';
        return data;
    }

    // Set blowoff
    feud.runBlowoff = add;

    // Add new gimmick match
    if (feudItem.consequence === 'new-gimmick-match') {
        if (add) {
            // Generate or get gimmick match
            let newGimmickMatch;
            if (feud.consequenceAppliedTo) {
                newGimmickMatch = (await getCompanyGimmickMatches(userCompany))
                    .find(x => x.name === feud.consequenceAppliedTo);
            }
            else {
                newGimmickMatch = generateGimmickMatch();
                await GimmickMatches.insert(newGimmickMatch);
                await addCompanyGimmickMatches(userCompany, [newGimmickMatch]);
                feud.consequenceAppliedTo = newGimmickMatch.name;
                data['newMatch'] = newGimmickMatch.name;
                data['participants'] = feudParticipants.map(x => x.name);
            }
            // Add to match
            if (newGimmickMatch) {
                // Remove existing
                let existingGimmickMatch = await getMatchGimmickMatch(match);
                if (existingGimmickMatch) {
                    userCompany.points += existingGimmickMatch.cost;
                }
                await addGimmickMatchMatches(newGimmickMatch, [match]);
            }
        }
        else {
            let existingGimmickMatch = await getMatchGimmickMatch(match);
            await removeGimmickMatchMatches(existingGimmickMatch, [match])
        }
    }

    // Add all available championships to match
    data['gimmickRestrictedChampionships'] = [];
    if (feudItem.consequence === 'unify-titles') {
        // Get championships to add to match
        let matchParticipants = await getMatchParticipants(match);
        let matchSuperstars = await getAllSuperstarsForMatch(match);
        let matchGimmick = await getMatchGimmickMatch(match);
        let matchEvent = await getMatchEvent(match);
        let availableChampionshipsData = await getMatchAvailableChampionships(matchParticipants, matchSuperstars,
            companyChampionships, matchGimmick, matchEvent);
        let availableChampionships = availableChampionshipsData['availableChampionships'];
        let championshipsToAdd = [];
        if (availableChampionships.length > 0) {
            if (add) {
                championshipsToAdd = availableChampionships;
                data['gimmickRestrictedChampionships'] = availableChampionshipsData['gimmickRestrictedChampionships'];
            }
            else {
                // Revert to first championship when removing blowoff
                championshipsToAdd = [availableChampionships[0]]
            }
        }

        // Remove match's championships
        let matchChampionships = await getMatchChampionships(match);
        for (const championship of matchChampionships) {
            await removeMatchChampionships(match, [championship]);
            for (const x of companyChampionships.filter(y => checkAreChampionshipsEqual(y, championship))) {
                x.booked = false;
            }
        }

        // Add championships to match
        for (const championship of championshipsToAdd) {
            await addMatchChampionships(match, [championship]);
            for (const x of companyChampionships.filter(y => checkAreChampionshipsEqual(y, championship))) {
                x.booked = true;
            }
        }
    }

    // Check tutorial action
    if (userCompany.storyName === 'tutorial') {
        data['nextTutorialState'] = await checkToGoToNextTutorialState(userCompany, ['add_feud_blowoff']);
    }

    db.saveDatabase();

    // Get data to return
    data['match'] = await getMatchData({match: match, companyChampionships: companyChampionships});
    data['points'] = userCompany.points;

    return data;
}

async function checkFeudBlowoffMatchParticipantType(feudParticipants, matchParticipants, matchType, consequence) {  // check_feud_blowoff_match_participant_type
    // Only allow blowoff if match type is same as feud participant type, except if no consequence
    if (!consequence) {
        return true;
    }

    const feudParticipantType = feudParticipants[0].objectName;

    if (feudParticipantType === 'superstar') {
        if (matchType !== 'singles') {
            return false;
        }
    }

    if (feudParticipantType === 'tagTeam') {
        if (matchType !== 'tag') {
            return false;
        }
        let participantSuperstars = await getTagTeamSuperstars(matchParticipants[0]);

        if (feudParticipants[0].trio) {
            // Allow trios feud blowoff in tag match (trio tagteam)
            if (![2, 3].includes(participantSuperstars.length)) {
                return false;
            }
        } else {
            if (participantSuperstars.length !== 2) {
                return false;
            }
        }
    }

    if (feudParticipantType === 'faction') {
        if (matchType !== 'tag') {
            return false;
        }
    }

    return true;
}

async function runFeudConsequence(feud, winner, loser, company, mainCompany, game, mainRoster, indieRoster, match,
                                  month, year, matchGraphics, matchType='singles') {  // run_feud_consequence
    const data = {};
    let feudPotentials = [];
    let feudHypeIncreased = [];
    let vacated = [];
    let retirements = [];
    let fired = [];
    let unlockedChampionshipSegmentItems = [];
    let maskRemoved = [];
    let factionConsequences = [];
    let factionReputationsData = [];
    let disbanded = [];
    const feudParticipants = await getFeudParticipants(feud);
    const feudItem = await getFeudFeudItem(feud);
    const companyChampionships = await getCompanyChampionships(company);
    const staffMembers = await getCompanyStaff(company);

    // Get trio if trio tagteam wins tag match
    if (matchType === 'tag' && winner.trioTagTeam) {
        let winnerSuperstar = (await getTagTeamSuperstars(winner))[0];
        let winnerTrio = (await getSuperstarTagTeams(winnerSuperstar)).find(x => x.trio);
        if (winnerTrio) {
            winner = winnerTrio
        }
    }

    if (feudItem.consequence === 'retirement') {

        // Get superstars to retire
        let participantsToRetire;
        if (feud.consequenceAppliedTo) {
            let consequenceAppliedTo = feudParticipants.filter(y => y.name === feud.consequenceAppliedTo);
            if (!objectInArray(winner, consequenceAppliedTo)) {
                participantsToRetire = consequenceAppliedTo;
            } else {
                participantsToRetire = [];
                data['careerNotEnded'] = feud.consequenceAppliedTo;
            }
        } else {
            participantsToRetire = [loser];
        }

        // Get all members of tag team
        if (matchType === 'tag' && participantsToRetire) {
            participantsToRetire = (await Promise.all(participantsToRetire.map(x => getRosterMemberSuperstars(x)))).flat();
        }

        for (const superstar of participantsToRetire) {
            if (superstar.activeEffect !== 'traded') {
                await processRetirementConsequence(superstar)
            }
        }

        if (retirements.length > 0) {
            await createRetirementFeud()
        }
    }

    if (feudItem.consequence === 'main-event-push') {
        let superstar = winner;
        let increase = superstar.popularity >= 800 ? 10 : 30;
        superstar.popularity += increase;
        data['pushed'] = superstar.name;
    }

    if (feudItem.consequence === 'unavailable') {

        // Get superstars to make unavailable
        let participant;
        if (feud.consequenceAppliedTo) {
            let consequenceAppliedTo = feudParticipants.filter(y => y.name === feud.consequenceAppliedTo);
            if (!objectInArray(winner, consequenceAppliedTo)) {
                participant = consequenceAppliedTo[0];
            }
        } else {
            participant = loser;
        }

        if (participant) {
            // Make unavailable
            data['forcedToLeave'] = participant.name;
            data['forcedToLeaveType'] = participant.objectName;
            let unavailableMonths = getRandomInt(2, 4);
            for (const s of (await getRosterMemberSuperstars(participant))) {
                addKayfabeInjuryToSuperstar(s, unavailableMonths);
                vacated.push(...(await vacateChampionships({superstar: s, championships: companyChampionships, month, year,
                    company, checkTalentRequest: true})));
                await checkTalentRequestAction({company: company, requestType: 'write_off', talent: s});
            }

            // Add feud potential
            let feudParticipants = [winner, loser];
            const feudItems = (await getCompanyFeudItems(company))
                .filter(x => x.unlocked && x.requirement === 'loser-leaves');
            let feudItem = getRandomChoice(feudItems);
            let newFeud = Feud();
            await Feuds.insert(newFeud);
            newFeud.name = feudItem.name;
            newFeud.hype = feudItem.baseHype;
            if (matchType === 'singles') {
                await addFeudSuperstars(newFeud, feudParticipants)
            }
            else if (matchType === 'tag') {
                await addFeudTagTeams(newFeud, feudParticipants)
            }
            newFeud.participants = feudParticipants.map(s => s.name);
            newFeud.directFeudEvent = feudItem.requirement;
            newFeud.startMonth = month;
            newFeud.startYear = year;
            feudPotentials.push({
                'name': newFeud.name,
                'participants': feudParticipants.map(x => x.name)
            });
            await addFeudItemFeuds(feudItem, [newFeud]);
            await addRosterFeuds(mainRoster, [newFeud]);
        }
    }

    if (feudItem.consequence === 'fired') {

        // Remove superstar from their roster and add to indie roster
        let superstars = (await getRosterMemberSuperstars(loser)).filter(x => x.activeEffect !== 'traded');
        const overnessDecrease = getRandomInt(8, 12);
        for (const superstar of superstars) {
            // Move to indies
            if (game.settings['all_indies_available']) {
                let indieAvailableRoster = await getCompanyRosterByType(mainCompany, 'indieAvailable');
                await addRosterSuperstars(indieAvailableRoster, [superstar]);
            } else {
                await addRosterSuperstars(indieRoster, [superstar]);
            }

            // Lower overness
            superstar.overness -= overnessDecrease;

            fired.push({
                'name': superstar.name,
                'pronoun': superstar.possessivePronoun,
                'overnessDecrease': overnessDecrease
            });

            // Remove teams/managers/factions
            disbanded.push(...await removeSuperstarTagTeams(superstar));
            await removeSuperstarFactionRelationships(superstar, month, year);
            await removeSuperstarManagerRelationships(superstar);
            superstar.brandName = null;

            // Vacate championships
            vacated.push(...(await vacateChampionships({superstar, championships: companyChampionships, month, year})));

            // Delete all relationships, add former company relationship
            for (const relationship of (await getSuperstarRelationships(superstar))) {
                await Relationships.remove(relationship)
            }
            await addFormerCompanyRelationship(superstar, company);

            // Delete talent requests
            for (const talentRequest of (await getSuperstarTalentRequests(superstar))) {
                await TalentRequests.remove(talentRequest)
            }

            // Make superstar happy
            changeSuperstarMoraleToHappy(superstar);

            // Add news
            let newsItem = await generateNewsItem({
                company, month: game.newsMonth, year: game.newsYear, superstar, newsType: 'superstar_released_by_company'
            });
            await addCompanyNewsItems(mainCompany, [newsItem]);

            if (company.userControlled) {
                // Add message
                const newMessage = await createTalentReleasedMessage(superstar, staffMembers, month, year);
                await addCompanyMessages(company, [newMessage]);
            }

            if (company.storyName === 'passion') {
                await processPowSuperstarLeavesCompany(superstar, company);
            }
        }
    }

    if (feudItem.consequence === 'disband-tag-team') {

        // Get tag team
        let tagTeam;
        for (const t of (await getSuperstarTagTeams(winner))) {
            if (!t.temporary) {
                if (checkAreObjArraysEqual((await getTagTeamSuperstars(t)), [winner, loser])) {
                    tagTeam = t;
                }
            }
        }

        if (tagTeam) {
            // Vacate championships
            const tagTeamSuperstars = await getTagTeamSuperstars(tagTeam);
            const vacatedChampionships = await vacateChampionships({superstar: tagTeamSuperstars[0],
                championships: companyChampionships, month, year, tagTeam: tagTeam});
            vacated.push(...vacatedChampionships);

            // Remove tag team
            await removeTagTeam(tagTeam);

            disbanded.push(tagTeam.name);

            // Check talent request
            for (const superstar of tagTeamSuperstars) {
                let requestData = {'teamId': tagTeam.$loki};
                await checkTalentRequestAction({company: company, requestType: 'disband_team', talent: superstar, data: requestData});
            }
        }
    }

    if (feudItem.consequence === 'unify-titles') {
        const matchChampionships = await getMatchChampionships(match);
        if (matchChampionships.length > 1) {
            data['unifiedChampionships'] = await unifyChampionships(matchChampionships, company, companyChampionships, game);
        }
    }

    if (feudItem.consequence === 'loser-turns-heel') {
        // Check if face lost
        const consequenceAppliedTo = feudParticipants.filter(y => y.name === feud.consequenceAppliedTo);
        if (consequenceAppliedTo.length > 0) {
            let participant = consequenceAppliedTo[0];
            let winnerParticipant = winner;
            if (winnerParticipant.objectName === 'tagTeam' && winnerParticipant.factionTagTeam) {
                let winnerSuperstar = (await getTagTeamSuperstars(winnerParticipant))[0];
                winnerParticipant = (await getSuperstarFactions(winnerSuperstar)).find(x => !x.disbanded);
            }
            if ((await getRosterMemberSuperstars(participant)).every(s => s.alignment !== 'heel')) {
                if (!objectInArray(winnerParticipant, consequenceAppliedTo)) {
                    await turnRosterMember(participant, 'heel', company);
                    data['turnedHeel'] = participant.name;
                } else {
                    data['didNotTurnHeel'] = participant.name;
                }
            }
        }
    }

    if (feudItem.consequence === 'winner-turns-face') {
        // Check if heel won
        let consequenceAppliedTo = feudParticipants.filter(y => y.name === feud.consequenceAppliedTo);
        if (consequenceAppliedTo.length > 0) {
            let participant = consequenceAppliedTo[0];
            let winnerParticipant = winner;
            if (winnerParticipant.objectName === 'tagTeam' && winnerParticipant.factionTagTeam) {
                let winnerSuperstar = (await getTagTeamSuperstars(winnerParticipant))[0];
                winnerParticipant = (await getSuperstarFactions(winnerSuperstar)).find(x => !x.disbanded);
            }
            if ((await getRosterMemberSuperstars(participant)).every(s => s.alignment !== 'face')) {
                if (objectInArray(winnerParticipant, consequenceAppliedTo)) {
                    await turnRosterMember(participant, 'face', company);
                    data['turnedFace'] = participant.name;
                } else {
                    data['didNotTurnFace'] = participant.name;
                }
            }
        }
    }

    if (feudItem.consequence === 'retirement-or-unmasking') {
        // Remove mask
        if (checkSuperstarGraphicMask(loser.graphic)) {
            loser.graphic = removeSuperstarGraphicMask(loser.graphic);
            maskRemoved.push(loser.name);

            // Update graphics
            if (matchGraphics) {
                matchGraphics = updateMatchOrSegmentGraphics(matchGraphics, loser);
            }

            // Check talent request
            await checkTalentRequestAction({company: company, requestType: 'change_appearance', talent: loser});
        }
        else {
            // Get superstars to retire
            let participantsToRetire = [loser];
            for (const superstar of participantsToRetire) {
                if (superstar.activeEffect !== 'traded') {
                    await processRetirementConsequence(superstar)
                }
            }

            if (retirements.length > 0) {
                await createRetirementFeud()
            }
        }
    }

    if (feudItem.consequence === 'disband-faction') {
        let loserSuperstar = (await getTagTeamSuperstars(loser))[0];
        let losingFaction = (await getSuperstarFactions(loserSuperstar)).find(x => !x.disbanded);
        if (losingFaction) {
            await disbandFaction(losingFaction, month, year);
            disbanded.push(losingFaction.name);
        }
    }

    if (feudItem.consequence === 'join-faction') {
        const faction = (await getSuperstarFactions(winner)).find(x => !x.disbanded);
        if (faction) {
            const factionSuperstars = await getFactionSuperstars(faction);

            // Check if loser is not present in faction and faction has less than maximum members
            if (!objectInArray(loser, factionSuperstars) && factionSuperstars.length < gameLimits.maxFactionSuperstars) {

                // Remove loser from their faction, if any
                const losersFaction =  (await getSuperstarFactions(loser)).find(x => !x.disbanded);
                if (losersFaction) {
                    await processLeaveFaction(losersFaction, loser);
                }

                // Update faction rep (members)
                const factionData = await processFactionXpChangeForFaction({faction: faction, addedMembers: [loser]});
                if (factionData) {
                    factionReputationsData.push(factionData);
                }

                // Add loser to winner's faction
                await addFactionSuperstars(faction, [loser]);
                factionConsequences.push({'name': loser.name, 'faction': faction.name, 'consequence': 'join_faction'});

                // Check join faction request
                let requestData = {'factionId': faction.$loki};
                await checkTalentRequestAction({company: company, requestType: 'join_faction', talent: loser,
                    data: requestData});
            }

            if (factionSuperstars.length >= gameLimits.maxFactionSuperstars) {
                factionConsequences.push(
                    {'name': loser.name, 'faction': faction.name, 'consequence': 'did_not_join_faction'});
            }
        }
    }

    if (feudItem.consequence === 'leave-faction') {
        const faction = (await getSuperstarFactions(winner)).find(x => !x.disbanded);
        if (faction) {
            await processLeaveFaction(faction, loser);
        }
    }

    if (feudItem.consequence === 'become-faction-leader') {
        const faction = (await getSuperstarFactions(winner)).find(x => !x.disbanded);
        if (faction) {
            setFactionLeaderSuperstar(faction, winner);
            factionConsequences.push({
                'name': winner.name, 'faction': faction.name, 'consequence': 'become_faction_leader'
            });
        }
    }

    data['feudPotentials'] = feudPotentials;
    data['feudHypeIncreased'] = feudHypeIncreased;
    data['vacated'] = vacated;
    data['retirements'] = retirements;
    data['fired'] = fired;
    data['unlockedChampionshipSegmentItems'] = unlockedChampionshipSegmentItems;
    data['maskRemoved'] = maskRemoved;
    data['graphics'] = matchGraphics;
    data['factionConsequences'] = factionConsequences;
    data['factionReputationsData'] = factionReputationsData;
    data['disbanded'] = disbanded;

    async function processRetirementConsequence(superstar) {
        // Add Are You Proud feud potential between winner superstar and loser's partner
        const superstarTeams = (await getSuperstarTagTeams(superstar)).filter(x => !x.temporary);
        if (superstarTeams.length > 0 && winner.objectName === 'superstar') {

            // Get partner superstar
            let partnerSuperstar;
            for (const t of superstarTeams) {
                for (const s of (await getTagTeamSuperstars(t))) {
                    if (!objectInArray(s, [winner, superstar])) {
                        partnerSuperstar = s;
                        break;
                    }
                }
            }

            if (partnerSuperstar) {
                const newFeudSuperstars = [winner, partnerSuperstar];
                const feudItems = (await getCompanyFeudItems(company))
                    .filter(x => x.unlocked && x.requirement === 'career-ended');
                const feudItem = getRandomChoice(feudItems);

                // Check for existing feud
                let existingFeud;
                for (const y of newFeudSuperstars) {
                    for (const f of (await getRosterMemberFeuds(y))) {
                        if ((f.started && !f.ended) || (!f.started && f.directFeudEvent && !f.ended)) {
                            if ((await getFeudParticipants(f)).every(y => objectInArray(y, newFeudSuperstars))) {
                                existingFeud = f;
                                break;
                            }
                        }
                    }
                }

                if (!existingFeud) {
                    // Add feud potential
                    const newFeud = Feud();
                    await Feuds.insert(newFeud);
                    newFeud.name = feudItem.name;
                    newFeud.hype = feudItem.baseHype;
                    newFeud.participants = newFeudSuperstars.map(s => s.name);
                    newFeud.directFeudEvent = feudItem.requirement;
                    newFeud.startMonth = month;
                    newFeud.startYear = year;
                    feudPotentials.push({
                        'name': newFeud.name,
                        'participants': newFeudSuperstars.map(s => s.name)
                    });
                    await addFeudItemFeuds(feudItem, [newFeud]);
                    await addRosterFeuds(mainRoster, [newFeud]);
                    await addFeudSuperstars(newFeud, newFeudSuperstars);
                } else {
                    const hypeIncrease = Math.floor(feudItem.baseHype / 2);
                    existingFeud.hype += hypeIncrease;
                    feudHypeIncreased.push({
                        'name': existingFeud.name,
                        'participants': newFeudSuperstars.map(s => s.name),
                        'hypeIncrease': hypeIncrease,
                        'newFeud': feudItem.name,
                    });
                }
            }
        }

        // Retire superstar
        const result = await retireSuperstar(superstar, mainRoster, companyChampionships, month, year, company);
        retirements.push(result);
    }

    async function createRetirementFeud() {
        // Add Not Done Yet feud potential
        const feudItems = (await getCompanyFeudItems(company))
            .filter(x => x.unlocked && x.requirement === 'retirement-feud');
        const feudItem = getRandomChoice(feudItems);
        const newFeud = Feud();
        await Feuds.insert(newFeud);
        newFeud.name = feudItem.name;
        newFeud.hype = feudItem.baseHype;
        newFeud.participants = [winner.name, loser.name];
        newFeud.directFeudEvent = feudItem.requirement;
        newFeud.startMonth = month;
        newFeud.startYear = year;
        feudPotentials.push({'name': newFeud.name, 'participants': [winner.name, loser.name]});
        await addFeudItemFeuds(feudItem, [newFeud]);
        await addRosterFeuds(mainRoster, [newFeud]);
        await addFeudSuperstars(newFeud, [winner, loser]);
    }

    async function processLeaveFaction(f, s) {
        // Update faction rep (members)
        const factionData = await processFactionXpChangeForFaction({faction: f, removedMembers: [s]});
        if (factionData) {
            factionReputationsData.push(factionData);
        }

        // Remove superstars from faction
        await removeFactionSuperstars(f, [s]);
        factionConsequences.push({'name': s.name, 'faction': f.name, 'consequence': 'leave_faction'});

        // Disband faction if less than 2 superstars remain
        if ((await getFactionSuperstars(f)).length < 2) {
            await disbandFaction(f, month, year);
            disbanded.push(f.name);
        }

        // Check leave faction request
        let requestData = {'factionId': f.$loki};
        await checkTalentRequestAction({company: company, requestType: 'leave_faction', talent: s,
            data: requestData});
    }

    return data;
}

export {getFeudData, getViewFeudsData, getFeudHistoryData, endViewFeudsFeud, getAllTalentData,
    getCreateFeudItemsData, generateRandomFeudItemName, createFeud, getFeudParticipantsNames, getFeudRating, endFeud,
    getRosterMemberFeuds, getFeudsForBooking, getFeudSegmentsInformation, getPreviousFeudHypeBoost, getParticipantFeuds,
    getAvailableFeudsForMatch, addFeudToMatchOrSegment, removeFeudFromMatchOrSegment, changeFeudBlowoff,
    getMatchesForStaleFeud, getAvailableFeudItems, getFeudCreationPreviousFeudsBoost, getFeudConsequenceAppliedTo,
    getFeudCreationBoosts, checkFeudBlowoffMatchParticipantType, runFeudConsequence}